import { useEffect, useState, useCallback } from "react";
import { ScrollText, RefreshCw, Loader2, Filter, User, Clock } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const ACTION_LABELS = {
  login: { label: "Accesso al portale", tone: "emerald" },
  fpcon_change: { label: "FPCON aggiornato", tone: "amber" },
  band_open: { label: "Bando aperto", tone: "gold" },
  band_close: { label: "Bando chiuso", tone: "muted" },
  exam_auth_approved: { label: "Esame · approvato", tone: "emerald" },
  exam_auth_rejected: { label: "Esame · rifiutato", tone: "rose" },
  exam_auth_delete: { label: "Esame · richiesta eliminata", tone: "rose" },
  inactivity_summon: { label: "Convocazione inattivi", tone: "orange" },
  user_update: { label: "Utente modificato", tone: "gold" },
  user_delete: { label: "Utente licenziato", tone: "rose" },
  user_create: { label: "Utente creato", tone: "emerald" },
};

/**
 * Etichetta best-effort per azioni generiche generate dal middleware audit,
 * quando non abbiamo una label semantica specifica.
 */
function labelFor(action) {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const [method, ...rest] = action.split("_");
  const path = rest.join("_").replace(/:id/g, "").replace(/\/+/g, " / ").trim();
  const methodMap = {
    post: { verb: "Creazione", tone: "emerald" },
    put: { verb: "Sostituzione", tone: "gold" },
    patch: { verb: "Modifica", tone: "gold" },
    delete: { verb: "Eliminazione", tone: "rose" },
  };
  const m = methodMap[method] || { verb: method.toUpperCase(), tone: "muted" };
  return { label: `${m.verb} · ${path || "root"}`, tone: m.tone };
}

const TONE_CLASSES = {
  emerald: "bg-emerald-500/10 border-emerald-400/40 text-emerald-200",
  rose: "bg-rose-500/10 border-rose-400/40 text-rose-200",
  gold: "bg-sheriff-gold/10 border-sheriff-gold/40 text-sheriff-gold",
  amber: "bg-amber-500/10 border-amber-400/40 text-amber-200",
  orange: "bg-orange-500/10 border-orange-400/40 text-orange-200",
  muted: "bg-white/5 border-white/15 text-[#B8A269]",
};

export default function AuditLogPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "150");
      if (filter) params.set("action", filter);
      if (emailFilter) params.set("actor_email", emailFilter);
      const { data } = await api.get(`/executive/audit-log?${params.toString()}`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento log");
    } finally {
      setLoading(false);
    }
  }, [filter, emailFilter]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-6" data-testid="audit-log-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label text-sheriff-gold flex items-center gap-2">
            <ScrollText className="h-4 w-4" /> Registro attività
          </div>
          <h2 className="font-heading font-black text-2xl text-[#F5E6B8] mt-1">Chi ha fatto cosa</h2>
          <p className="text-sm text-[#B8A269] max-w-2xl mt-1">
            Storico delle azioni critiche eseguite dal Comando e dalle Risorse Umane. Utile per
            audit interni e revisione delle decisioni.
          </p>
        </div>
        <Button onClick={reload} disabled={loading} variant="outline" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Ricarica
        </Button>
      </div>

      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 flex items-center gap-1"><Filter className="h-3 w-3" /> Filtra per azione</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="audit-log-action-filter"
            className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold"
          >
            <option value="">Tutte</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="uppercase-label text-[10px] text-sheriff-gold mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Filtra per email autore</label>
          <Input
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="es. sheriff@lssd.local"
            data-testid="audit-log-email-filter"
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8]"
          />
        </div>
      </div>

      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="p-8 text-center text-[#B8A269]">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Caricamento log…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-[#8B7B49] italic">
            Nessuna azione registrata con i filtri attuali.
          </div>
        ) : (
          <div className="divide-y divide-sheriff-gold/10">
            {items.map((it) => {
              const meta = labelFor(it.action);
              const tone = TONE_CLASSES[meta.tone] || TONE_CLASSES.muted;
              return (
                <div key={it.id} className="p-4 hover:bg-sheriff-gold/5" data-testid={`audit-log-row-${it.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold border ${tone} shrink-0`}>
                        {meta.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[#F5E6B8] text-sm font-medium">
                          {it.actor_name || it.actor_email}
                          {it.actor_role && (
                            <span className="text-[#B8A269] text-xs ml-2">· {it.actor_role}</span>
                          )}
                        </div>
                        {it.target && (
                          <div className="text-xs text-[#D4C480] mt-0.5">
                            Target: <span className="text-sheriff-gold">{it.target}</span>
                          </div>
                        )}
                        {it.details && (
                          <div className="text-[11px] text-[#B8A269] mt-1 font-mono break-all">
                            {Object.entries(it.details).filter(([, v]) => v !== null && v !== undefined && v !== "").slice(0, 5).map(([k, v]) => (
                              <span key={k} className="inline-block mr-3">
                                <span className="opacity-60">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v).slice(0, 80)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono text-[#8B7B49] flex items-center gap-1 shrink-0 whitespace-nowrap">
                      <Clock className="h-3 w-3" />
                      {it.at ? new Date(it.at).toLocaleString("it-IT") : "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
